import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box, Typography, Button, TextField, MenuItem, Switch, FormControlLabel,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  useTheme, alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddIcon from "@mui/icons-material/Add";
import { ACCENT } from "./accent";
import { getCatalog, simulate, createCombination } from "./api/propulsion";
import { PART_META } from "./partSpecs";
import AddPartDialog from "./AddPartDialog";
import ResultsView from "./ResultsView";
import { sweepModeled } from "./demo/model";
import { APP_BASE } from "./paths";
import { comboPath } from "./paths";

const motorLabel = (m) => `${m.brand} ${m.name}${m.kv ? ` · ${m.kv}Kv` : ""}`;
const propLabel = (p) => `${p.name}${p.diameter_in ? ` · ${p.diameter_in}×${p.pitch_in}` : ""}`;
const escLabel = (e) => `${e.name}${e.max_current_a ? ` · ${e.max_current_a}A` : ""}`;
const batLabel = (b) => `${b.name}`;

const BuilderPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // selected part ids
  const [sel, setSel] = useState({ motor_id: "", propeller_id: "", esc_id: "", battery_id: "" });
  const [form, setForm] = useState({
    all_up_weight_g: 850, num_motors: 4, coaxial: false,
    cells_series: 4, field_elevation_m: 0, air_temperature_c: 15,
  });

  const [result, setResult] = useState(null);
  const [curve, setCurve] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState(null);

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // which "Add part" dialog is open (motor | propeller | esc | battery | null)
  const [addType, setAddType] = useState(null);

  // A newly-added part is appended to its catalog list and auto-selected.
  const onPartCreated = (saved) => {
    const listKey = PART_META[addType].catalogKey;
    setCatalog((c) => ({ ...c, [listKey]: [...c[listKey], saved] }));
    setSel((s) => ({ ...s, [`${addType}_id`]: saved.id }));
    setAddType(null);
  };

  useEffect(() => {
    getCatalog()
      .then(setCatalog)
      .catch((e) => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const allPartsChosen = sel.motor_id && sel.propeller_id && sel.esc_id && sel.battery_id;

  const setPart = (key) => (e) => setSel((s) => ({ ...s, [key]: e.target.value }));
  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: key === "coaxial" ? e.target.checked : e.target.value }));

  const suggestedName = useMemo(() => {
    if (!catalog || !allPartsChosen) return "";
    const m = catalog.motors.find((x) => x.id === sel.motor_id);
    const p = catalog.propellers.find((x) => x.id === sel.propeller_id);
    return `${m?.name || "motor"} + ${p?.name || "prop"}`;
  }, [catalog, sel, allPartsChosen]);

  const run = useCallback(async () => {
    setSimLoading(true);
    setSimError(null);
    const opts = {
      all_up_weight_g: Number(form.all_up_weight_g),
      num_motors: Number(form.num_motors),
      coaxial: form.coaxial,
      cells_series: Number(form.cells_series),
      field_elevation_m: Number(form.field_elevation_m),
      air_temperature_c: Number(form.air_temperature_c),
    };
    try {
      const data = await simulate({ ...sel, ...opts });
      setResult(data.result);
      // Modeled reference curve for the charts, computed client-side from the
      // selected catalog parts (the backend returns a single operating point).
      try {
        const p = {
          motor: catalog.motors.find((m) => m.id === sel.motor_id),
          propeller: catalog.propellers.find((x) => x.id === sel.propeller_id),
          esc: catalog.escs.find((x) => x.id === sel.esc_id),
          battery: catalog.batteries.find((b) => b.id === sel.battery_id),
        };
        setCurve(sweepModeled(p, opts));
      } catch {
        setCurve(null);
      }
    } catch (e) {
      setSimError(e.message);
      setResult(null);
      setCurve(null);
    } finally {
      setSimLoading(false);
    }
  }, [sel, form, catalog]);

  const openSave = () => {
    setSaveName(suggestedName);
    setSaveError(null);
    setSaveOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const combo = await createCombination({
        name: saveName.trim() || suggestedName,
        motor_id: sel.motor_id,
        propeller_id: sel.propeller_id,
        esc_id: sel.esc_id,
        battery_id: sel.battery_id,
        cells_series: Number(form.cells_series),
      });
      setSaveOpen(false);
      navigate(comboPath(combo.id));
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const selectSx = { flex: "1 1 220px", minWidth: 200 };
  const fieldSx = { flex: "1 1 130px", minWidth: 120 };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Button
        component={RouterLink}
        to={APP_BASE}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ color: theme.palette.text.secondary, textTransform: "none", mb: 2 }}
      >
        Propulsion Calculator
      </Button>

      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        New calculation
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
        Pick a motor, propeller, ESC and battery, then run. Save it to add the combination to your list.
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress color="secondary" size={28} />
        </Box>
      ) : loadError ? (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {loadError}
        </Typography>
      ) : (
        <>
          {/* Part selectors — each has an inline "Add new" that opens the part dialog */}
          <Box
            sx={{
              display: "flex", flexWrap: "wrap", gap: 2, mt: 3, p: 2,
              borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            }}
          >
            {[
              { type: "motor", key: "motor_id", label: "Motor", list: catalog.motors, fmt: motorLabel },
              { type: "propeller", key: "propeller_id", label: "Propeller", list: catalog.propellers, fmt: propLabel },
              { type: "esc", key: "esc_id", label: "ESC", list: catalog.escs, fmt: escLabel },
              { type: "battery", key: "battery_id", label: "Battery", list: catalog.batteries, fmt: batLabel },
            ].map(({ type, key, label, list, fmt }) => (
              <Box key={key} sx={{ ...selectSx, display: "flex", flexDirection: "column", gap: 0.5 }}>
                <TextField size="small" select label={label} value={sel[key]} onChange={setPart(key)}>
                  {list.map((item) => (
                    <MenuItem key={item.id} value={item.id}>{fmt(item)}</MenuItem>
                  ))}
                </TextField>
                <Button
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setAddType(type)}
                  sx={{ alignSelf: "flex-start", color: ACCENT, textTransform: "none", minWidth: 0 }}
                >
                  Add new {label.toLowerCase()}
                </Button>
              </Box>
            ))}
          </Box>

          {/* Frame inputs + actions */}
          <Box
            sx={{
              display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mt: 2, p: 2,
              borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            }}
          >
            <TextField sx={fieldSx} size="small" type="number" label="All-up weight (g)"
              value={form.all_up_weight_g} onChange={setField("all_up_weight_g")} />
            <TextField sx={fieldSx} size="small" type="number" label="# Motors"
              value={form.num_motors} onChange={setField("num_motors")} />
            <TextField sx={fieldSx} size="small" type="number" label="Cells (S)"
              value={form.cells_series} onChange={setField("cells_series")} />
            <TextField sx={fieldSx} size="small" type="number" label="Elevation (m)"
              value={form.field_elevation_m} onChange={setField("field_elevation_m")} />
            <TextField sx={fieldSx} size="small" type="number" label="Air temp (°C)"
              value={form.air_temperature_c} onChange={setField("air_temperature_c")} />
            <FormControlLabel
              control={<Switch checked={form.coaxial} onChange={setField("coaxial")} size="small" />}
              label={<Typography variant="body2">Coaxial</Typography>}
            />
            <Button
              variant="contained"
              startIcon={simLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={run}
              disabled={!allPartsChosen || simLoading}
              sx={{ backgroundColor: ACCENT, textTransform: "none", "&:hover": { backgroundColor: "#0058A5" } }}
            >
              Run simulation
            </Button>
            <Button
              variant="outlined"
              startIcon={<SaveOutlinedIcon />}
              onClick={openSave}
              disabled={!allPartsChosen}
              sx={{ color: ACCENT, borderColor: ACCENT, textTransform: "none",
                "&:hover": { borderColor: ACCENT, backgroundColor: `${ACCENT}14` } }}
            >
              Save combination
            </Button>
          </Box>

          {simError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {simError}
            </Alert>
          )}

          <Box sx={{ mt: 3 }}>
            <ResultsView result={result} curve={curve} chartNote="Modeled reference curve (±~15%)" />
          </Box>
        </>
      )}

      {/* Add-part dialog (inline, from the selectors) */}
      <AddPartDialog
        open={Boolean(addType)}
        type={addType}
        onClose={() => setAddType(null)}
        onCreated={onPartCreated}
      />

      {/* Save dialog */}
      <Dialog open={saveOpen} onClose={() => !saving && setSaveOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Save combination</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            sx={{ mt: 1 }}
          />
          {saveError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {saveError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: ACCENT, textTransform: "none", "&:hover": { backgroundColor: "#0058A5" } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BuilderPage;
